import { useCallback, useEffect, useState } from 'react';
import {
  EngineError,
  getCaseOutreach,
  logCallAttempt,
  type PartyOutreach,
} from '../../api/engineDashboard';
import { partyRoleLabel } from '../../lib/labels';

interface Props {
  token: string;
  caseId: string;
  isCourt: boolean;
  /** Judges are read-only — no call logging. */
  readOnly: boolean;
  onUnauthorized: () => void;
}

const CHANNEL_LABELS: Record<string, string> = { email: 'Email', sms: 'Text', call: 'Call' };

const fmt = (iso: string) =>
  new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/**
 * Clerk-view parties + outreach dashboard (spec 1.3–1.6): role, name,
 * contacts with missing-field flags, per-channel attempt counts, and the
 * attempt list as METADATA ONLY — date, channel, delivery status. No
 * message content exists anywhere in this payload by construction.
 */
export function ClerkOutreachPanel({ token, caseId, isCourt, readOnly, onUnauthorized }: Props) {
  const [parties, setParties] = useState<PartyOutreach[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logging, setLogging] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(() => {
    getCaseOutreach(token, caseId)
      .then((res) => {
        const rank = (a: string) => (a === 'claimant' ? 0 : a === 'respondent' ? 1 : 2);
        setParties([...res.parties].sort((a, b) => rank(a.role_archetype) - rank(b.role_archetype)));
      })
      .catch((err: unknown) => {
        if (err instanceof EngineError && err.status === 401) return onUnauthorized();
        setError('Could not load outreach.');
      });
  }, [token, caseId, onUnauthorized]);

  useEffect(load, [load]);

  const logCall = async (partyId: string) => {
    setLogging(partyId);
    try {
      await logCallAttempt(token, caseId, partyId);
      load();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not log the call.');
    } finally {
      setLogging(null);
    }
  };

  return (
    <section className="card p-4">
      <div className="section-label">Parties &amp; outreach</div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      {parties === null ? (
        <p className="mt-2 text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="mt-2 space-y-3">
          {parties.map((p) => {
            const roleLabel = partyRoleLabel(p, isCourt ? 'court' : null);
            const missing = [
              !p.email ? 'email missing' : null,
              !p.phone ? 'phone missing' : null,
            ].filter(Boolean) as string[];
            const open = expanded.has(p.party_id);
            return (
              <div key={p.party_id} className="rounded-md border border-surface-border p-3">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="text-sm font-semibold">
                    {roleLabel}: {p.name}
                  </span>
                  {missing.map((m) => (
                    <span key={m} className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
                      ⚠ {m}
                    </span>
                  ))}
                </div>
                {(p.email || p.phone) && (
                  <div className="mt-0.5 text-xs text-gray-500">
                    {[p.phone, p.email].filter(Boolean).join(' · ')}
                  </div>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-700">
                  <span>✉ {p.counts.email} Email{p.counts.email === 1 ? '' : 's'}</span>
                  <span>💬 {p.counts.sms} Text{p.counts.sms === 1 ? '' : 's'}</span>
                  <span>📞 {p.counts.call} Call{p.counts.call === 1 ? '' : 's'}</span>
                  {p.attempts.length > 0 && (
                    <button
                      type="button"
                      className="text-brand hover:underline"
                      onClick={() =>
                        setExpanded((prev) => {
                          const next = new Set(prev);
                          if (next.has(p.party_id)) next.delete(p.party_id);
                          else next.add(p.party_id);
                          return next;
                        })
                      }
                    >
                      {open ? 'Hide attempts' : `Show ${p.attempts.length} attempt${p.attempts.length === 1 ? '' : 's'}`}
                    </button>
                  )}
                  {!readOnly && (
                    <button
                      type="button"
                      className="ml-auto text-gray-500 hover:text-gray-900"
                      disabled={logging === p.party_id}
                      title="Record that a staff member attempted to call this party"
                      onClick={() => void logCall(p.party_id)}
                    >
                      {logging === p.party_id ? 'Logging…' : '+ Log call'}
                    </button>
                  )}
                </div>
                {open && (
                  <ul className="mt-2 space-y-0.5 border-t border-surface-border pt-2 text-xs text-gray-600">
                    {p.attempts.map((a, i) => (
                      <li key={i}>
                        {fmt(a.at)} — {CHANNEL_LABELS[a.channel] ?? a.channel}
                        <span className="text-gray-400"> · {a.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          {parties.length === 0 && <p className="text-sm text-gray-500">No parties on this case.</p>}
        </div>
      )}
    </section>
  );
}
